import React, { useState, useEffect } from "react";
import { Users, Video, Mic, MicOff, Heart, Play, Eye, Send, Clock, Timer, TrendingUp } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import IntentIcon from "./IntentIcon";

interface Participant {
  id: string;
  name: string;
  photo_url: string | null;
}

interface WaitTimeEstimate {
  pending_ahead: number;
  current_participants: number;
  max_participants: number;
  is_active: boolean;
  estimated_minutes: number;
  avg_response_seconds: number;
}

interface BoothPeekModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJoin: () => void;
  onRequestJoin?: () => void;
  requiresApproval?: boolean;
  hasPendingRequest?: boolean;
  booth: {
    id: string;
    name: string;
    topic: string;
    exhibition_intent: string | null;
    participants_count: number;
    max_participants: number;
    host_name: string | null;
    host_photo: string | null;
    city_name: string | null;
    country_flag: string | null;
    participant_previews: Participant[];
  };
}

// Audio visualization bars component
const AudioVisualization: React.FC<{ isActive: boolean; size?: "sm" | "md" }> = ({ 
  isActive, 
  size = "sm" 
}) => {
  const barCount = size === "sm" ? 3 : 4;
  const barHeight = size === "sm" ? "h-2" : "h-3";
  const barWidth = size === "sm" ? "w-0.5" : "w-1";
  
  return (
    <div className={cn("flex items-end gap-0.5", size === "sm" ? "h-2" : "h-3")}>
      {Array.from({ length: barCount }).map((_, idx) => (
        <div
          key={idx}
          className={cn(
            barWidth,
            "bg-green-400 rounded-full transition-all",
            isActive 
              ? "animate-pulse" 
              : "opacity-30"
          )}
          style={{
            height: isActive 
              ? `${Math.random() * 60 + 40}%` 
              : "20%",
            animationDelay: `${idx * 100}ms`,
            animationDuration: `${300 + idx * 50}ms`,
          }}
        />
      ))}
    </div>
  );
};

// Speaking indicator component with simulated activity
const SpeakingIndicator: React.FC<{ participantId: string }> = ({ participantId }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevels, setAudioLevels] = useState([0.2, 0.2, 0.2, 0.2]);

  // Simulate random speaking activity
  useEffect(() => {
    const speakingInterval = setInterval(() => {
      // Random chance to toggle speaking state
      const shouldSpeak = Math.random() > 0.6;
      setIsSpeaking(shouldSpeak);
    }, 2000 + Math.random() * 2000);

    return () => clearInterval(speakingInterval);
  }, [participantId]);

  // Animate audio levels when speaking
  useEffect(() => {
    if (!isSpeaking) {
      setAudioLevels([0.2, 0.2, 0.2, 0.2]);
      return;
    }

    const audioInterval = setInterval(() => {
      setAudioLevels([
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
      ]);
    }, 100);

    return () => clearInterval(audioInterval);
  }, [isSpeaking]);

  return (
    <div className={cn(
      "absolute inset-0 rounded-lg transition-all duration-300 pointer-events-none",
      isSpeaking && "ring-2 ring-green-400 ring-offset-1 ring-offset-gray-900"
    )}>
      {/* Speaking glow effect */}
      {isSpeaking && (
        <div className="absolute inset-0 rounded-lg bg-green-400/10 animate-pulse" />
      )}
      
      {/* Audio level bars */}
      <div className="absolute bottom-1.5 left-1.5 flex items-end gap-0.5 h-3">
        {audioLevels.map((level, idx) => (
          <div
            key={idx}
            className={cn(
              "w-0.5 rounded-full transition-all duration-100",
              isSpeaking ? "bg-green-400" : "bg-gray-500"
            )}
            style={{ height: `${level * 100}%` }}
          />
        ))}
      </div>
      
      {/* Speaking badge */}
      {isSpeaking && (
        <div className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-green-500 text-[8px] font-bold text-white shadow-lg animate-bounce">
          SPEAKING
        </div>
      )}
    </div>
  );
};

const BoothPeekModal: React.FC<BoothPeekModalProps> = ({
  isOpen,
  onClose,
  onJoin,
  onRequestJoin,
  requiresApproval = true, // Exhibition booths require approval by default
  hasPendingRequest = false,
  booth,
}) => {
  const [requestSent, setRequestSent] = useState(hasPendingRequest);
  const [waitTimeEstimate, setWaitTimeEstimate] = useState<WaitTimeEstimate | null>(null);
  const [loadingWaitTime, setLoadingWaitTime] = useState(false);
  
  const isFull = booth.participants_count >= booth.max_participants;
  const isHot = booth.participants_count >= booth.max_participants * 0.7;

  // Fetch wait time estimate when modal opens or request is sent
  useEffect(() => {
    const fetchWaitTime = async () => {
      if (!isOpen || !booth.id) return;
      
      setLoadingWaitTime(true);
      try {
        const { data, error } = await supabase.rpc('get_booth_wait_time_estimate', {
          booth_uuid: booth.id
        });
        
        if (error) throw error;
        setWaitTimeEstimate(data as unknown as WaitTimeEstimate);
      } catch (error) {
        console.error('Error fetching wait time:', error);
      } finally {
        setLoadingWaitTime(false);
      }
    };

    fetchWaitTime();
  }, [isOpen, booth.id, requestSent]);

  const handleRequestJoin = () => {
    if (onRequestJoin) {
      onRequestJoin();
    }
    setRequestSent(true);
    toast({
      title: "📨 Request Sent!",
      description: `Your request to join "${booth.name}" has been sent to the host.`,
    });
  };

  const handleDirectJoin = () => {
    onJoin();
    onClose();
  };

  const formatWaitTime = (minutes: number): string => {
    if (minutes < 0) return "Booth inactive";
    if (minutes < 1) return "< 1 min";
    if (minutes < 60) return `~${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `~${hours}h ${mins}m`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800">
        {/* Header */}
        <div className="relative p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            {booth.host_photo ? (
              <img
                src={booth.host_photo}
                alt={booth.host_name || "Host"}
                className="w-12 h-12 rounded-full object-cover ring-2 ring-pink-500"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center ring-2 ring-pink-500">
                <Heart className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white truncate">{booth.name}</h3>
                {booth.exhibition_intent && (
                  <IntentIcon intent={booth.exhibition_intent} size="sm" showLabel={false} />
                )}
              </div>
              <p className="text-sm text-gray-400 truncate">
                {booth.city_name && booth.country_flag
                  ? `${booth.country_flag} ${booth.city_name}`
                  : booth.topic}
              </p>
              {booth.host_name && (
                <p className="text-xs text-gray-500">Hosted by {booth.host_name}</p>
              )}
            </div>
          </div>

          {/* Live indicator */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/20 border border-red-500/30">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            <span className="text-xs font-medium text-red-400">LIVE</span>
          </div>
        </div>

        {/* Participant Grid */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-300">Peeking Inside</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <Users className="w-4 h-4 text-gray-400" />
              <span className={cn(
                "font-medium",
                isHot ? "text-amber-400" : "text-gray-300"
              )}>
                {booth.participants_count}
              </span>
              <span className="text-gray-500">/ {booth.max_participants}</span>
            </div>
          </div>

          {/* Video Grid with Speaking Indicators */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {booth.participant_previews.map((participant, idx) => (
              <div
                key={participant.id}
                className="relative aspect-square rounded-lg overflow-hidden bg-gray-800 group"
              >
                {participant.photo_url ? (
                  <img
                    src={participant.photo_url}
                    alt={participant.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600">
                    <span className="text-2xl font-bold text-white">
                      {participant.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Simulated video overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Speaking indicator overlay */}
                <SpeakingIndicator participantId={participant.id} />

                {/* Mic indicator */}
                <div className="absolute bottom-1.5 right-1.5 p-1 rounded-full bg-black/50">
                  <Mic className="w-2.5 h-2.5 text-green-400" />
                </div>

                {/* Video indicator */}
                <div className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/50">
                  <Video className="w-2.5 h-2.5 text-white/80" />
                </div>

                {/* Name overlay */}
                <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-[10px] text-white font-medium truncate text-center">
                    {participant.name}
                  </p>
                </div>

                {/* Host badge */}
                {idx === 0 && (
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-500 text-white">
                    HOST
                  </div>
                )}
              </div>
            ))}

            {/* Empty slots */}
            {booth.participant_previews.length < 6 &&
              Array.from({ length: Math.min(6 - booth.participant_previews.length, 3) }).map((_, idx) => (
                <div
                  key={`empty-${idx}`}
                  className="aspect-square rounded-lg bg-gray-800/50 border border-dashed border-gray-700 flex items-center justify-center"
                >
                  <Users className="w-6 h-6 text-gray-600" />
                </div>
              ))}
          </div>

          {/* Room info */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-800/50 mb-4">
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Video On</span>
            </div>
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Audio On</span>
            </div>
            {booth.exhibition_intent && (
              <div className="flex items-center gap-2 ml-auto">
                <IntentIcon intent={booth.exhibition_intent} size="sm" showLabel />
              </div>
            )}
          </div>

          {/* Pending request notice with wait time */}
          {requestSent && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-400 animate-pulse" />
                <span className="text-sm font-medium text-amber-300">
                  Request Pending
                </span>
              </div>
              
              {loadingWaitTime ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-amber-300/70">Calculating wait time...</span>
                </div>
              ) : waitTimeEstimate ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 text-amber-300/80">
                      <Timer className="w-3 h-3" />
                      <span>Estimated wait:</span>
                    </div>
                    <span className="font-bold text-amber-200">
                      {formatWaitTime(waitTimeEstimate.estimated_minutes)}
                    </span>
                  </div>
                  
                  {waitTimeEstimate.pending_ahead > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 text-amber-300/80">
                        <Users className="w-3 h-3" />
                        <span>Requests ahead:</span>
                      </div>
                      <span className="font-medium text-amber-200">
                        {waitTimeEstimate.pending_ahead}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 text-amber-300/80">
                      <TrendingUp className="w-3 h-3" />
                      <span>Booth capacity:</span>
                    </div>
                    <span className="font-medium text-amber-200">
                      {waitTimeEstimate.current_participants}/{waitTimeEstimate.max_participants}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-amber-300/70">
                  Waiting for host approval...
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Close
            </Button>
            
            {requiresApproval ? (
              <Button
                onClick={requestSent ? undefined : handleRequestJoin}
                disabled={isFull || requestSent}
                className={cn(
                  "flex-1 font-medium",
                  isFull || requestSent
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                )}
              >
                {isFull ? (
                  <>Booth Full</>
                ) : requestSent ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Pending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Request to Join
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleDirectJoin}
                disabled={isFull}
                className={cn(
                  "flex-1 font-medium",
                  isFull
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                )}
              >
                <Play className="w-4 h-4 mr-2" />
                {isFull ? "Booth Full" : "Join Booth"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoothPeekModal;
